/*
  Bobochic · « Essayez-le chez vous » — plan à l'échelle d'un canapé ou d'un lit dans la pièce du client.
  Custom element <pk-room-fit>, rendu par sections/pk-room-fit.liquid.

  Accepts : un <script type="application/json" data-prf-config> enfant, construit en Liquid à partir des
            métachamps du produit (dimensions, forme, couchage, pouf, tailles de lit par variante).
  Dependencies : assets/pk-room-fit.css
  Usage : <script type="module" src="{{ 'pk-room-fit.js' | asset_url }}"></script> (chargé par la section).

  Portage de la maquette « Aura-t-il sa place » (canvas Design) : mêmes calculs (encombrement pivoté,
  marges, verdicts, porte), mêmes interactions (glisser, pivoter, aimant aux murs, appui long sur - / +).
*/

const I18N = {
  fr: {
    titleSofa: 'Essayez-le dans votre salon',
    titleBed: 'Essayez-le dans votre chambre',
    introSofa: 'Indiquez les dimensions de votre salon : la longueur du mur où il ira, puis la profondeur de la pièce. Nous dessinons le canapé à l’échelle.',
    introBed: 'Indiquez les dimensions de votre chambre : la largeur du mur côté tête de lit, puis la longueur de la pièce. Nous dessinons le lit à l’échelle.',
    wallSofa: 'Longueur du mur', wallSofaHelp: 'Le mur du canapé, d’angle à angle.',
    roomSofa: 'Profondeur de la pièce', roomShortSofa: 'Profondeur',
    wallBed: 'Largeur du mur', wallBedHelp: 'Le mur côté tête de lit.',
    roomBed: 'Longueur de la pièce', roomShortBed: 'Longueur',
    roomHelp: 'Jusqu’au mur d’en face.',
    example: 'Ex. {v}',
    specLength: 'Longueur', specDepth: 'Profondeur', specWidth: 'Largeur', specSleep: 'Couchage',
    sleepLabel: 'Couchage {l} × {w} cm',
    meridienne: 'Méridienne', left: 'À gauche', right: 'À droite',
    config: 'Configuration', configSeat: 'Canapé', configBed: 'Déplié',
    bedSize: 'Taille du lit', pouf: 'Pouf', bench: 'Bout de lit', without: 'Sans', with: 'Avec',
    hinge: 'Ouverture de la porte', hingeTop: 'En haut', hingeBottom: 'En bas',
    doorWidth: 'Largeur de la porte',
    doorHelp: 'Faites glisser la porte sur le plan pour la placer sur le bon mur.',
    doorHelpMobile: 'Glissez la porte sur le plan pour la placer.',
    dragSofa: 'Faites glisser le canapé et la porte pour les placer.',
    dragBed: 'Faites glisser le lit et la porte pour les placer.',
    dragSofaMobile: 'Glissez le canapé et la porte pour les placer.',
    dragBedMobile: 'Glissez le lit et la porte pour les placer.',
    rotate: 'Pivoter', rotLeft: 'Pivoter de 15 degrés vers la gauche', rotRight: 'Pivoter de 15 degrés vers la droite',
    minus10: 'Retirer 10 cm', plus10: 'Ajouter 10 cm', minus5: 'Retirer 5 cm', plus5: 'Ajouter 5 cm',
    schemaSofa: 'Voir toutes les dimensions du canapé', schemaBed: 'Voir toutes les dimensions du lit',
    schemaTitleSofa: 'Dimensions du canapé {name}', schemaTitleBed: 'Dimensions du lit {name}',
    schemaAlt: 'Schéma coté de {name}',
    close: 'Fermer', openOpts: 'Modifier la pièce et les options', optsTitle: 'Votre pièce et les options', seeResult: 'Voir le résultat',
    sumWall: 'Mur', sumRoom: 'Pièce', sumMeri: 'Méridienne', sumLeft: 'à gauche', sumRight: 'à droite',
    sumBed: 'Lit', sumUnfolded: 'déplié', sumPouf: 'Pouf', sumBench: 'Bout de lit', yes: 'oui', no: 'non', sumDoor: 'Porte',
    plusPouf: ' + pouf', plusBench: ' + bout de lit',
    door: 'Porte {v}',
    doorAria: 'Porte de {v} sur le mur {w}. Faites-la glisser le long des murs, ou utilisez les flèches.',
    walls: { top: 'du haut', bottom: 'du bas', left: 'de gauche', right: 'de droite' },
    sofaAria: '{kind} {name}{unf}, {l} à gauche, {r} à droite. Pivoté de {a} degrés. Flèches pour le déplacer, R pour le pivoter de 15 degrés.',
    kindSofa: 'Canapé', kindBed: 'Lit', unfolded: ' déplié',
    frontSofa: ' de passage', footBed: ' au pied du lit', footBench: ' devant le bout de lit',
    emptyT: 'Indiquez les dimensions de votre pièce', emptyM: 'Le plan se met à jour à l’échelle à chaque centimètre.',
    partialT: 'Renseignez aussi {what}', partialM: 'Les deux dimensions sont nécessaires pour vérifier qu’il rentre.',
    partialRoomSofa: 'la profondeur de la pièce', partialWallSofa: 'la longueur du mur',
    partialRoomBed: 'la longueur de la pièce', partialWallBed: 'la largeur du mur',
    koT: 'Il ne rentre pas', koTBed: 'Déplié, il ne rentre pas',
    tightT: 'Il passe tout juste', tightTBed: 'Déplié, il passe tout juste',
    okT: 'Il trouve sa place', okTBed: 'Déplié, il trouve sa place',
    koBoth: 'Il dépasse de {x} en largeur et de {y} en profondeur.',
    koW: 'Il dépasse de {x} en largeur.', koD: 'Il dépasse de {y} en profondeur.',
    koRotated: ' Remettez-le contre le mur ou choisissez un modèle plus compact.',
    koTryRotate: ' Essayez de le pivoter, ou choisissez un modèle plus compact.',
    koNeedRoom: ' Le couchage a besoin de plus de recul.',
    tightM: '{a} de marge en largeur et {b} de passage au mieux. Prévoyez environ 15 cm de chaque côté et {p} cm pour circuler.',
    okM: '{a} de marge en largeur et jusqu’à {b} de passage. Laissez environ 15 cm de chaque côté pour qu’il respire.',
    bedKoT: 'Il ne rentre pas',
    bedKoBoth: 'Il dépasse de {x} en largeur et de {y} en longueur.',
    bedKoW: 'Il dépasse de {x} en largeur. Essayez une taille plus petite.', bedKoD: 'Il dépasse de {y} en longueur.',
    bedTightT: 'Il passe tout juste', bedTightM: '{a} de chaque côté et {b}{foot}. Pensez au passage autour du lit.',
    bedOkT: 'Il rentre dans votre chambre', bedOkM: '{a} de chaque côté et {b}{foot}.',
    doorT: 'Il bloque la porte', doorM: 'La porte de {v} ne peut plus s’ouvrir en entier. Déplacez le {kind} ou la porte sur le plan.',
    kindSofaLow: 'canapé', kindBedLow: 'lit',
    altSofa: 'Voir des canapés plus compacts', altBed: 'Voir des lits plus compacts'
  },
  nl: {
    titleSofa: 'Probeer hem uit in uw woonkamer',
    titleBed: 'Probeer het uit in uw slaapkamer',
    introSofa: 'Geef de afmetingen van uw woonkamer op: de lengte van de muur waar hij komt te staan, en daarna de diepte van de kamer. Wij tekenen de bank op schaal.',
    introBed: 'Geef de afmetingen van uw slaapkamer op: de breedte van de muur aan de kant van het hoofdbord, en daarna de lengte van de kamer. Wij tekenen het bed op schaal.',
    wallSofa: 'Lengte van de muur', wallSofaHelp: 'De muur waar de bank tegen komt, van hoek tot hoek.',
    roomSofa: 'Diepte van de kamer', roomShortSofa: 'Diepte',
    wallBed: 'Breedte van de muur', wallBedHelp: 'De muur aan de kant van het hoofdbord.',
    roomBed: 'Lengte van de kamer', roomShortBed: 'Lengte',
    roomHelp: 'Tot aan de muur ertegenover.',
    example: 'Bv. {v}',
    specLength: 'Lengte', specDepth: 'Diepte', specWidth: 'Breedte', specSleep: 'Ligvlak',
    sleepLabel: 'Ligvlak {l} × {w} cm',
    meridienne: 'Longchair', left: 'Links', right: 'Rechts',
    config: 'Configuratie', configSeat: 'Bank', configBed: 'Uitgeklapt',
    bedSize: 'Bedmaat', pouf: 'Poef', bench: 'Voetenbank', without: 'Zonder', with: 'Met',
    hinge: 'Draairichting van de deur', hingeTop: 'Boven', hingeBottom: 'Onder',
    doorWidth: 'Breedte van de deur',
    doorHelp: 'Sleep de deur op het plan naar de juiste muur.',
    doorHelpMobile: 'Sleep de deur op het plan om hem te plaatsen.',
    dragSofa: 'Sleep de bank en de deur om ze te plaatsen.',
    dragBed: 'Sleep het bed en de deur om ze te plaatsen.',
    dragSofaMobile: 'Sleep de bank en de deur om ze te plaatsen.',
    dragBedMobile: 'Sleep het bed en de deur om ze te plaatsen.',
    rotate: 'Draaien', rotLeft: '15 graden naar links draaien', rotRight: '15 graden naar rechts draaien',
    minus10: '10 cm verwijderen', plus10: '10 cm toevoegen', minus5: '5 cm verwijderen', plus5: '5 cm toevoegen',
    schemaSofa: 'Bekijk alle afmetingen van de bank', schemaBed: 'Bekijk alle afmetingen van het bed',
    schemaTitleSofa: 'Afmetingen van de bank {name}', schemaTitleBed: 'Afmetingen van het bed {name}',
    schemaAlt: 'Maattekening van {name}',
    close: 'Sluiten', openOpts: 'Kamer en opties aanpassen', optsTitle: 'Uw kamer en opties', seeResult: 'Bekijk het resultaat',
    sumWall: 'Muur', sumRoom: 'Kamer', sumMeri: 'Longchair', sumLeft: 'links', sumRight: 'rechts',
    sumBed: 'Bed', sumUnfolded: 'uitgeklapt', sumPouf: 'Poef', sumBench: 'Voetenbank', yes: 'ja', no: 'nee', sumDoor: 'Deur',
    plusPouf: ' + poef', plusBench: ' + voetenbank',
    door: 'Deur {v}',
    doorAria: 'Deur van {v} op de {w} muur. Sleep hem langs de muren of gebruik de pijltjestoetsen.',
    walls: { top: 'bovenste', bottom: 'onderste', left: 'linker', right: 'rechter' },
    sofaAria: '{kind} {name}{unf}, {l} links, {r} rechts. {a} graden gedraaid. Pijltjestoetsen om te verplaatsen, R om 15 graden te draaien.',
    kindSofa: 'Bank', kindBed: 'Bed', unfolded: ' uitgeklapt',
    frontSofa: ' doorloopruimte', footBed: ' aan het voeteneinde', footBench: ' voor de voetenbank',
    emptyT: 'Geef de afmetingen van uw kamer op', emptyM: 'Het plan wordt bij elke centimeter op schaal bijgewerkt.',
    partialT: 'Vul ook {what} in', partialM: 'Beide afmetingen zijn nodig om te controleren of alles past.',
    partialRoomSofa: 'de diepte van de kamer', partialWallSofa: 'de lengte van de muur',
    partialRoomBed: 'de lengte van de kamer', partialWallBed: 'de breedte van de muur',
    koT: 'Hij past niet', koTBed: 'Uitgeklapt past hij niet',
    tightT: 'Hij past maar net', tightTBed: 'Uitgeklapt past hij maar net',
    okT: 'Hij past', okTBed: 'Ook uitgeklapt past hij',
    koBoth: 'Hij is {x} te breed en {y} te diep.',
    koW: 'Hij is {x} te breed.', koD: 'Hij is {y} te diep.',
    koRotated: ' Zet hem weer recht tegen de muur of kies een compacter model.',
    koTryRotate: ' Probeer hem te draaien of kies een compacter model.',
    koNeedRoom: ' Uitgeklapt heeft hij meer ruimte nodig.',
    tightM: '{a} speling in de breedte en op zijn best {b} doorloopruimte. Voorzie ongeveer 15 cm aan elke kant en {p} cm om vrij te kunnen lopen.',
    okM: '{a} speling in de breedte en tot {b} doorloopruimte. Laat ongeveer 15 cm aan elke kant vrij, zo komt hij het best tot zijn recht.',
    bedKoT: 'Het past niet',
    bedKoBoth: 'Het is {x} te breed en {y} te lang.',
    bedKoW: 'Het is {x} te breed. Probeer een kleinere maat.', bedKoD: 'Het is {y} te lang.',
    bedTightT: 'Het past maar net', bedTightM: '{a} aan elke kant en {b}{foot}. Denk aan de doorgang rond het bed.',
    bedOkT: 'Het past in uw slaapkamer', bedOkM: '{a} aan elke kant en {b}{foot}.',
    doorT: 'De deur wordt geblokkeerd', doorM: 'De deur van {v} kan niet meer volledig open. Verplaats {kind} of de deur op het plan.',
    kindSofaLow: 'de bank', kindBedLow: 'het bed',
    altSofa: 'Bekijk compactere banken', altBed: 'Bekijk compactere bedden'
  }
};

function tpl(str, map) {
  return String(str).replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
}
function r1(v) { return Math.round(v * 10) / 10; }
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function cm(v) { return Math.round(v) + ' cm'; }
function num(str) { const v = parseInt(str, 10); return (isNaN(v) || v < 50) ? 0 : Math.min(v, 2000); }
function px(v) { return v + 'px'; }

// Dimensions du pouf lues dans le texte Dimensions de la fiche, sous ses différentes formes :
// « Longueur pouf : 100 cm / Largeur pouf : 70 cm », « Dimensions du pouf : Longueur : 100 cm / Largeur : 70 cm »,
// « Largeur du pouf : 100 cm / Profondeur : 70 cm », « Pouf : L. 100 x P. 100 x H. 44 cm » (et équivalents NL)
function parsePouf(txt) {
  if (!txt) return null;
  const t = String(txt).toLowerCase().replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ');
  const i = t.search(/pouf|poef/);
  if (i < 0) return null;
  let w = t.slice(Math.max(0, i - 20));
  const j = w.search(/colis|pakket/);
  if (j > 0) w = w.slice(0, j);
  const m = w.match(/l\.\s*(\d{2,3})\s*x\s*[pl]\.\s*(\d{2,3})/);
  if (m) return { w: Math.max(+m[1], +m[2]), d: Math.min(+m[1], +m[2]) };
  const get = (re) => { const r = w.match(re); return r ? +r[1] : 0; };
  const lon = get(/(?:longueur|lengte)[^\d]{0,20}?(\d{2,3})/);
  const lar = get(/(?:largeur|breedte)[^\d]{0,20}?(\d{2,3})/);
  const pro = get(/(?:profondeur|diepte)[^\d]{0,20}?(\d{2,3})/);
  if (lon && lar) return { w: Math.max(lon, lar), d: Math.min(lon, lar) };
  if (lar && pro) return { w: lar, d: pro };
  const one = lon || lar || pro;
  return one ? { w: one, d: 0 } : null;
}

class PkRoomFit extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    const cfgEl = this.querySelector('script[data-prf-config]');
    if (!cfgEl) return;
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    this._ready = true;
    const c = this.cfg;
    this.T = I18N[c.lang] || I18N.fr;
    this.IS_BED = c.mode === 'bed';
    // Pouf : correction manuelle, sinon dimensions lues sur la fiche, sinon réglages de la section
    const PS = c.sofa;
    if (PS && PS.pouf) {
      const pp = parsePouf(PS.poufText) || {};
      PS.poufWidth = PS.poufWidth || pp.w || PS.poufDefaultWidth;
      PS.poufDepth = PS.poufDepth || pp.d || PS.poufDefaultDepth;
    }

    // Tailles de lit : une entrée par couchage distinct (« 160x200 »), avec la valeur d'option à cliquer sur la page
    this.sizes = [];
    if (this.IS_BED) {
      const seen = {};
      Object.keys(c.beds || {}).forEach((id) => {
        const b = c.beds[id];
        if (!b || b[0] < 50 || b[1] < 50) return;
        const k = b[0] + 'x' + b[1];
        if (seen[k]) return;
        seen[k] = 1;
        const parts = String((c.variants || {})[id] || '').split('|');
        this.sizes.push({ key: k, w: b[0], l: b[1], opt: c.sizeIndex >= 0 ? parts[c.sizeIndex] : null });
      });
      this.sizes.sort((a, b) => a.w - b.w || a.l - b.l);
      if (!this.sizes.length) { this.hidden = true; return; }
    }

    this.state = {
      wall: '', room: '', dir: null, px: null, py: null, ang: 0, bed: false, size: null,
      bench: !!c.bench, pouf: false, doorW: '83', doorWall: 'bottom', doorPos: null, doorHinge: 'start',
      dragging: false, doorDragging: false, schema: false, opts: false
    };
    this.lastVariant = this.variantId();
    this.bind();
    this.render(true);
    requestAnimationFrame(() => this.stage.classList.add('is-ready'));

    this._poll = setInterval(() => {
      const v = this.variantId();
      if (v !== this.lastVariant) {
        this.lastVariant = v;
        this.setState({ dir: null, size: null });
      }
    }, 400);
    if (window.ResizeObserver) {
      let lastW = 0;
      this._ro = new ResizeObserver(() => {
        const w = this.stageBox.clientWidth;
        if (w && Math.abs(w - lastW) > 2) { lastW = w; this.render(true); }
      });
      this._ro.observe(this.stageBox);
    } else {
      window.addEventListener('resize', () => this.render(true));
    }
  }

  disconnectedCallback() {
    clearInterval(this._poll);
    if (this._ro) this._ro.disconnect();
    this.holdEnd();
  }

  /* ── Variantes de la fiche (Prestige) ── */
  variantId() {
    const c = this.cfg, vars = c.variants || {};
    const u = new URLSearchParams(window.location.search).get('variant');
    if (u && vars[u]) return u;
    const f = document.querySelector('form[id^="product-form-main-' + c.productId + '-"]:not([id$="-payment-installment"]) [name="id"]');
    if (f && f.value && vars[f.value]) return f.value;
    return String(c.defaultVariant);
  }
  variantDir() {
    const s = String((this.cfg.variants || {})[this.variantId()] || '').toLowerCase();
    if (s.indexOf('gauche') > -1 || s.indexOf('links') > -1) return 'left';
    if (s.indexOf('droit') > -1 || s.indexOf('rechts') > -1) return 'right';
    return null;
  }
  variantSize() {
    const b = (this.cfg.beds || {})[this.variantId()];
    if (b && b[0] >= 50 && b[1] >= 50) return b[0] + 'x' + b[1];
    return null;
  }
  pageScope() { return document.querySelector('.shopify-section--main-product') || document; }
  // Clique sur l'option correspondante du sélecteur de variante, sans faire sauter la page
  clickPageOption(match) {
    const y = window.pageYOffset;
    const keep = () => { if (Math.abs(window.pageYOffset - y) > 30) window.scrollTo({ top: y, left: 0, behavior: 'instant' }); };
    try {
      const scope = this.pageScope();
      const radios = scope.querySelectorAll('input[type="radio"]');
      for (let i = 0; i < radios.length; i++) {
        if (match(radios[i].value) && !radios[i].checked) { radios[i].click(); break; }
      }
      const opts = scope.querySelectorAll('select option');
      for (let j = 0; j < opts.length; j++) {
        if (match(opts[j].value) && !opts[j].selected) {
          const sel = opts[j].parentNode; sel.value = opts[j].value;
          sel.dispatchEvent(new Event('change', { bubbles: true })); break;
        }
      }
    } catch (e) {}
    [0, 60, 150, 300, 600].forEach((t) => setTimeout(keep, t));
  }
  pickDirOnPage(dir) {
    const wants = dir === 'right' ? ['droit', 'rechts'] : ['gauche', 'links'];
    this.clickPageOption((v) => { v = String(v || '').toLowerCase(); return wants.some((w) => v.indexOf(w) > -1); });
  }

  /* ── DOM ── */
  q(name) { return this.querySelector('[data-r="' + name + '"]'); }
  bind() {
    const T = this.T, B = this.IS_BED;
    this.querySelectorAll('[data-i18n]').forEach((el) => {
      const k = el.dataset.i18n;
      if (T[k] !== undefined) el.textContent = T[k];
    });
    this.querySelectorAll('[data-i18n-aria]').forEach((el) => {
      const k = el.dataset.i18nAria;
      if (T[k] !== undefined) { el.setAttribute('aria-label', T[k]); el.setAttribute('title', T[k]); }
    });
    this.q('title').textContent = B ? T.titleBed : T.titleSofa;
    this.q('intro').textContent = B ? T.introBed : T.introSofa;
    this.q('wallLabel').textContent = B ? T.wallBed : T.wallSofa;
    this.q('wallHelp').textContent = B ? T.wallBedHelp : T.wallSofaHelp;
    this.q('roomLabel').textContent = B ? T.roomBed : T.roomSofa;
    this.q('spec1').textContent = B ? T.specWidth : T.specLength;
    this.q('spec2').textContent = B ? T.specLength : T.specDepth;
    const sch = this.q('schemaBtn');
    if (sch) { const l = B ? T.schemaBed : T.schemaSofa; sch.setAttribute('aria-label', l); sch.setAttribute('title', l); }
    const dwTitle = this.q('schemaTitle');
    if (dwTitle) dwTitle.textContent = tpl(B ? T.schemaTitleBed : T.schemaTitleSofa, { name: this.cfg.name });
    const img = this.q('schemaImg');
    if (img) img.alt = tpl(T.schemaAlt, { name: this.cfg.title });

    this.stage = this.q('stage');
    this.stageBox = this.q('stageBox');
    this.sofaEl = this.q('sofa');
    this.piecesEl = this.q('pieces');
    this.wallInput = this.q('wallInput');
    this.roomInput = this.q('roomInput');
    this.doorInput = this.q('doorInput');

    // Champs : mise à jour à chaque frappe
    this.wallInput.addEventListener('input', (e) => this.setState({ wall: e.target.value }));
    this.roomInput.addEventListener('input', (e) => this.setState({ room: e.target.value }));
    this.doorInput.addEventListener('input', (e) => this.setState({ doorW: e.target.value }));

    // Boutons - / + : un pas au clic, défilement après une seconde d'appui
    this.querySelectorAll('[data-step]').forEach((b) => {
      const key = b.dataset.step, d = parseInt(b.dataset.dir, 10);
      b.addEventListener('pointerdown', (e) => {
        if (e.button > 0) return;
        this.holdEnd();
        this.stepOnce(key, d);
        this.holdT = setTimeout(() => { this.holdI = setInterval(() => this.stepOnce(key, d), 70); }, 1000);
      });
      ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => b.addEventListener(ev, () => this.holdEnd()));
      // Clavier (Entrée / Espace) : le clic arrive sans appui de pointeur
      b.addEventListener('click', (e) => { if (e.detail === 0) this.stepOnce(key, d); });
    });

    const on = (name, fn) => { const el = this.q(name); if (el) el.addEventListener('click', fn); };
    on('dirLeft', () => this.pickDir('left'));
    on('dirRight', () => this.pickDir('right'));
    on('cfgSeat', () => this.setBed(false));
    on('cfgBed', () => this.setBed(true));
    on('poufOff', () => this.setPouf(false));
    on('poufOn', () => this.setPouf(true));
    on('benchOff', () => this.setBench(false));
    on('benchOn', () => this.setBench(true));
    on('hingeA', () => this.setState({ doorHinge: 'start' }));
    on('hingeB', () => this.setState({ doorHinge: 'end' }));
    on('rotLeft', () => this.setAngle(this.g.angRaw - 15, this.g.cx, this.g.cy));
    on('rotRight', () => this.setAngle(this.g.angRaw + 15, this.g.cx, this.g.cy));
    on('schemaBtn', () => this.openLayer('schema'));
    this.querySelectorAll('[data-close="schema"]').forEach((el) => el.addEventListener('click', () => this.closeLayer('schema')));
    on('openOpts', () => this.openLayer('opts'));
    this.querySelectorAll('[data-close="opts"]').forEach((el) => el.addEventListener('click', () => this.closeLayer('opts')));
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.state.schema) this.closeLayer('schema');
      else if (this.state.opts) this.closeLayer('opts');
    });

    // Tailles de lit
    const sizesBox = this.q('sizes');
    if (sizesBox) {
      if (this.sizes.length < 2) this.q('sizeGroup').hidden = true;
      this.sizeBtns = this.sizes.map((z) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'prf-choice';
        b.textContent = z.w + ' × ' + z.l;
        b.addEventListener('click', () => this.setSize(z));
        sizesBox.appendChild(b);
        return b;
      });
    }

    // Canapé : glisser, flèches, R pour pivoter
    const sofa = this.sofaEl;
    sofa.addEventListener('pointerdown', (e) => {
      if (e.button > 0) return;
      this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, px: this.g.posX, py: this.g.posY, k: this.g.s * this.stageRatio() };
      try { sofa.setPointerCapture(e.pointerId); } catch (err) {}
      this.setState({ dragging: true });
    });
    sofa.addEventListener('pointermove', (e) => {
      const d = this.drag;
      if (!d || d.id !== e.pointerId) return;
      this.moveTo(d.px + (e.clientX - d.x) / d.k, d.py + (e.clientY - d.y) / d.k);
    });
    const dragEnd = (e) => {
      if (!this.drag) return;
      this.drag = null;
      try { sofa.releasePointerCapture(e.pointerId); } catch (err) {}
      this.setState({ dragging: false });
    };
    sofa.addEventListener('pointerup', dragEnd);
    sofa.addEventListener('pointercancel', dragEnd);
    sofa.addEventListener('keydown', (e) => {
      const k = e.key, st = e.shiftKey ? 20 : 5, g = this.g;
      if (k === 'r' || k === 'R') { e.preventDefault(); this.setAngle(g.angRaw + (e.shiftKey ? -15 : 15), g.cx, g.cy); return; }
      const dx = k === 'ArrowLeft' ? -st : k === 'ArrowRight' ? st : 0;
      const dy = k === 'ArrowUp' ? -st : k === 'ArrowDown' ? st : 0;
      if (!dx && !dy) return;
      e.preventDefault();
      this.moveTo(g.posX + dx, g.posY + dy);
    });

    // Porte : glisser le long des murs, flèches
    const door = this.q('door');
    door.addEventListener('pointerdown', (e) => {
      if (e.button > 0) return;
      const st = this.stage.getBoundingClientRect();
      this.ddrag = { id: e.pointerId, left: st.left, top: st.top, k: this.stageRatio() };
      try { door.setPointerCapture(e.pointerId); } catch (err) {}
      this.setState({ doorDragging: true });
    });
    door.addEventListener('pointermove', (e) => {
      const d = this.ddrag, g = this.g;
      if (!d || d.id !== e.pointerId) return;
      const x = ((e.clientX - d.left) / d.k - g.rx0) / g.s, y = ((e.clientY - d.top) / d.k - g.y0) / g.s;
      const dist = { top: Math.abs(y), bottom: Math.abs(g.roomD - y), left: Math.abs(x), right: Math.abs(g.roomW - x) };
      const w = Object.keys(dist).sort((a, b) => dist[a] - dist[b])[0];
      const along = (w === 'top' || w === 'bottom') ? x : y;
      const len = (w === 'top' || w === 'bottom') ? g.roomW : g.roomD;
      this.setState({ doorWall: w, doorPos: Math.round(clamp(along - g.dw / 2, 0, Math.max(0, len - g.dw))) });
    });
    const doorEnd = (e) => {
      if (!this.ddrag) return;
      this.ddrag = null;
      try { door.releasePointerCapture(e.pointerId); } catch (err) {}
      this.setState({ doorDragging: false });
    };
    door.addEventListener('pointerup', doorEnd);
    door.addEventListener('pointercancel', doorEnd);
    door.addEventListener('keydown', (e) => {
      const g = this.g, st = e.shiftKey ? 20 : 5, k = e.key, horiz = g.dWall === 'top' || g.dWall === 'bottom';
      const d = horiz ? (k === 'ArrowLeft' ? -st : k === 'ArrowRight' ? st : 0) : (k === 'ArrowUp' ? -st : k === 'ArrowDown' ? st : 0);
      if (!d) return;
      e.preventDefault();
      const len = horiz ? g.roomW : g.roomD;
      this.setState({ doorPos: Math.round(clamp(g.dPos + d, 0, Math.max(0, len - g.dw))) });
    });
  }

  stageRatio() {
    const w = this.stage.getBoundingClientRect().width;
    return (w && this.g.cw) ? w / this.g.cw : 1;
  }

  setState(patch) {
    Object.assign(this.state, patch);
    this.render(false);
  }

  /* ── Calques : schéma coté (tiroir) et options (feuille du bas, mobile) ── */
  openLayer(name) {
    this._returnFocus = document.activeElement;
    const o = {}; o[name] = true;
    this.setState(o);
    document.documentElement.classList.add('prf-lock');
    const panel = this.q(name === 'schema' ? 'schemaPanel' : 'form');
    const btn = panel && panel.querySelector('[data-close="' + name + '"].dw-close');
    if (btn) setTimeout(() => btn.focus(), 50);
  }
  closeLayer(name) {
    const o = {}; o[name] = false;
    this.setState(o);
    if (!this.state.schema && !this.state.opts) document.documentElement.classList.remove('prf-lock');
    if (this._returnFocus && this._returnFocus.focus) this._returnFocus.focus();
  }

  /* ── Actions ── */
  holdEnd() {
    if (this.holdT) clearTimeout(this.holdT);
    if (this.holdI) clearInterval(this.holdI);
    this.holdT = this.holdI = null;
  }
  stepOnce(key, d) {
    const g = this.g, o = {};
    if (key === 'door') o.doorW = String(clamp((g.dw || 83) + 5 * d, 40, 300));
    else {
      const cur = key === 'wall' ? g.wall : g.room, def = key === 'wall' ? g.DEF_WALL : g.DEF_ROOM;
      o[key] = String(clamp(Math.round(cur ? cur + 10 * d : def), 50, 2000));
    }
    this.setState(o);
  }
  pickDir(dir) {
    this.setState({ dir: dir });
    if (this.variantDir()) this.pickDirOnPage(dir);
  }
  // Aimant : à moins de 6 cm d'un mur, le produit s'y colle
  moveTo(nx, ny) {
    const g = this.g;
    const snap = (v, max) => (v < 6 ? 0 : (max - v < 6 ? max : Math.round(v)));
    this.setState({ px: g.freeX >= 0 ? snap(g.fitX(nx, g.fw), g.freeX) : null, py: g.freeY >= 0 ? snap(g.fitY(ny, g.fh), g.freeY) : 0 });
  }
  // Pivote autour du centre, puis garde le produit dans la pièce
  setAngle(na, ccx, ccy) {
    const g = this.g;
    na = Math.round(na);
    const nb = g.bbox(((na % 360) + 360) % 360, g.local, g.LX, g.DL);
    this.setState({ ang: na, px: Math.round(g.fitX(ccx - nb.ox, nb.w)), py: Math.round(g.fitY(ccy - nb.oy, nb.h)) });
  }
  // Change la profondeur (déplié, pouf, bout de lit, taille) en gardant le dos contre son mur
  changeDepth(patch, nd, nlx) {
    const g = this.g, lx = nlx || g.LX, d = (nd - g.DL) / 2;
    const ncx = g.cx - g.SIN * d, ncy = g.cy + g.COS * d, nb = g.bbox(g.ang, [[0, 0, lx, nd]], lx, nd);
    patch.px = Math.round(g.fitX(ncx - nb.ox, nb.w));
    patch.py = Math.round(g.fitY(ncy - nb.oy, nb.h));
    this.setState(patch);
  }
  setBed(on) { if (on !== this.g.bed) this.changeDepth({ bed: on }, this.g.sofaDepth(on, this.g.pouf)); }
  setPouf(on) { if (on !== this.g.pouf) this.changeDepth({ pouf: on }, this.g.sofaDepth(this.g.bed, on)); }
  setBench(on) { if (on !== this.g.bench) this.changeDepth({ bench: on }, this.g.bedDepth(this.g.SZ, on)); }
  setSize(z) {
    const g = this.g;
    if (z.key === g.SZ.key) return;
    this.changeDepth({ size: z.key }, g.bedDepth(z, g.bench), z.w + g.EXW);
    if (z.opt) { const want = String(z.opt).toLowerCase(); this.clickPageOption((v) => String(v || '').toLowerCase() === want); }
  }

  /* ── Rendu ── */
  render(noAnim) {
    const c = this.cfg, T = this.T, st = this.state, IS_BED = this.IS_BED, self = this;
    const mobile = window.matchMedia('(max-width: ' + (c.breakpoint - 1) + 'px)').matches;
    const NAME = c.name;
    this.classList.toggle('is-mobile', mobile);
    const TIGHT = c.tight, PASS = c.passMin;

    const angRaw = Math.round(Number(st.ang) || 0);
    const ang = ((angRaw % 360) + 360) % 360;
    const dir = st.dir || this.variantDir() || 'left';

    // Produit dessiné dans un repère local : LX le long du mur, profondeur DL (dos contre le mur du haut)
    let LX, DL, raw, label, sizeLabel, poufIdx = -1;
    const P = c.sofa || {};
    const L = P.length, D = P.depth, SHAPE = P.shape;
    const BACK = P.back, ARM = P.arm;
    const RW = Math.min(P.returnWidth, SHAPE === 'pano' ? L / 2 - 20 : L - ARM - 40);
    const BD = SHAPE === 'straight' ? D : Math.min(P.bodyDepth, D * 0.92);
    const CONV = !IS_BED && !!P.convertible;
    const bed = CONV && st.bed;
    const POUF_OK = !IS_BED && !!P.pouf;
    const PW = P.poufWidth, PD = P.poufDepth;
    const pouf = POUF_OK && st.pouf;
    const BEDW = P.bedWidth;
    const BEDL = Math.min(P.bedLength, SHAPE === 'straight' ? L - 2 * ARM : L - ARM);
    function sofaDepth(isBed, withPouf) {
      if (isBed) return Math.max(D, BACK + BEDW + (withPouf ? PD : 0));
      return withPouf ? Math.max(D, BD + PD) : D;
    }

    // Lit : couchage + surplus (cadre, tête de lit), bout de lit éventuel
    const EXW = c.bedFrame ? c.bedFrame.extraW : 0, EXL = c.bedFrame ? c.bedFrame.extraL : 0;
    const HB = c.bedFrame ? Math.min(c.bedFrame.head, EXL) : 0, PIL = 45;
    const BENCH_D = c.bench ? c.bench.depth : 0, BENCH_W = c.bench ? c.bench.width : 0, BENCH_GAP = 4;
    const bench = IS_BED && !!c.bench && st.bench;
    function bedDepth(sz, withBench) { return sz.l + EXL + (withBench ? BENCH_GAP + BENCH_D : 0); }
    let SZ = null;
    if (IS_BED) {
      const want = st.size || this.variantSize();
      SZ = this.sizes.find((z) => z.key === want) || this.sizes[0];
    }

    if (IS_BED) {
      const W = SZ.w, ML = SZ.l, FW = W + EXW, FL = ML + EXL, RAIL = EXW / 2, FOOT = Math.max(0, EXL - HB);
      LX = FW; DL = bedDepth(SZ, bench);
      raw = [[0, 0, FW, HB], [0, HB, RAIL, FL - HB], [FW - RAIL, HB, RAIL, FL - HB], [RAIL, FL - FOOT, W, FOOT],
        [RAIL, HB, W / 2, PIL], [RAIL + W / 2, HB, W / 2, PIL], [RAIL, HB + PIL, W, ML - PIL]];
      raw = raw.filter((q) => q[2] > 0.5 && q[3] > 0.5);
      if (bench) { const bw = BENCH_W || W + 6; raw.push([(FW - bw) / 2, FL + BENCH_GAP, bw, BENCH_D]); }
      label = [RAIL, HB + PIL, W, ML - PIL];
      sizeLabel = W + ' × ' + ML + ' cm';
    } else {
      LX = L; DL = sofaDepth(bed, pouf);
      raw = [[0, 0, L, BACK]];
      if (bed) {
        if (SHAPE === 'straight') {
          const bx = (L - BEDL) / 2;
          raw.push([0, BACK, ARM, D - BACK], [L - ARM, BACK, ARM, D - BACK], [bx, BACK, BEDL, BEDW]);
          label = [bx, BACK, BEDL, BEDW];
        } else {
          raw.push([0, BACK, BEDL, BEDW]);
          const endX = SHAPE === 'pano' ? L - RW : L - ARM;
          if (endX - BEDL > 4) raw.push([BEDL, BACK, endX - BEDL, BD - BACK]);
          if (SHAPE === 'pano') raw.push([L - RW, BACK, RW, D - BACK]);
          else raw.push([L - ARM, BACK, ARM, BD - BACK]);
          label = [0, BACK, BEDL, BEDW];
        }
        // Pouf posé devant le couchage déplié
        if (pouf) { raw.push([label[0] + (BEDL - PW) / 2, BACK + BEDW, PW, PD]); poufIdx = raw.length - 1; }
        sizeLabel = tpl(T.sleepLabel, { l: Math.round(P.bedLength), w: Math.round(BEDW) });
      } else {
        if (SHAPE === 'straight') {
          raw.push([0, BACK, ARM, D - BACK], [L - ARM, BACK, ARM, D - BACK]);
          const n = clamp(P.places || 3, 1, 4), w = (L - 2 * ARM) / n;
          for (let i = 0; i < n; i++) raw.push([ARM + i * w, BACK, w, D - BACK]);
          label = [ARM, BACK, L - 2 * ARM, D - BACK];
        } else if (SHAPE === 'pano') {
          const mid = L - 2 * RW, w = mid / 2;
          raw.push([0, BACK, RW, D - BACK], [L - RW, BACK, RW, D - BACK], [RW, BACK, w, BD - BACK], [RW + w, BACK, w, BD - BACK]);
          label = [RW, BACK, mid, BD - BACK];
        } else {
          const sw = (L - ARM - RW) / 2;
          raw.push([0, BACK, RW, D - BACK], [L - ARM, BACK, ARM, BD - BACK], [RW, BACK, sw, BD - BACK], [RW + sw, BACK, sw, BD - BACK]);
          label = [RW, BACK, L - ARM - RW, BD - BACK];
        }
        // Pouf posé devant l'assise
        if (pouf) { raw.push([label[0] + (label[2] - PW) / 2, BD, PW, PD]); poufIdx = raw.length - 1; }
        sizeLabel = Math.round(L) + ' × ' + Math.round(D) + ' cm';
      }
    }

    const DEF_WALL = IS_BED ? Math.ceil((LX + 120) / 10) * 10 : Math.ceil((L + 80) / 10) * 10;
    const DEF_ROOM = IS_BED ? Math.ceil((DL + 130) / 10) * 10 : Math.ceil((D + 260) / 10) * 10;
    const wallStr = st.wall === null ? String(DEF_WALL) : st.wall;
    const roomStr = st.room === null ? String(DEF_ROOM) : st.room;
    const wall = num(wallStr), room = num(roomStr);
    const hasW = wall > 0, hasR = room > 0;

    // Encombrement au sol (boîte englobante) du produit pivoté autour de son centre
    const mir = (r) => ((!IS_BED && SHAPE === 'corner' && dir === 'right') ? [LX - r[0] - r[2], r[1], r[2], r[3]] : r);
    function bbox(a, rects, lx, dl) {
      const t = a * Math.PI / 180, co = Math.cos(t), sn = Math.sin(t);
      let mnx = Infinity, mxx = -Infinity, mny = Infinity, mxy = -Infinity;
      rects.forEach((r) => {
        [[r[0], r[1]], [r[0] + r[2], r[1]], [r[0], r[1] + r[3]], [r[0] + r[2], r[1] + r[3]]].forEach((q) => {
          const x = q[0] - lx / 2, y = q[1] - dl / 2, X = x * co - y * sn, Y = x * sn + y * co;
          mnx = Math.min(mnx, X); mxx = Math.max(mxx, X); mny = Math.min(mny, Y); mxy = Math.max(mxy, Y);
        });
      });
      return { w: Math.round((mxx - mnx) * 10) / 10, h: Math.round((mxy - mny) * 10) / 10, ox: -mnx, oy: -mny };
    }
    const local = raw.map(mir);
    const BB = bbox(ang, local, LX, DL), fw = BB.w, fh = BB.h;
    const SIN = Math.sin(ang * Math.PI / 180), COS = Math.cos(ang * Math.PI / 180);

    // Échelle du plan : largeur réelle du cadre, hauteur plafonnée
    const bcs = getComputedStyle(this.stageBox);
    const boxW = (this.stageBox.clientWidth - parseFloat(bcs.paddingLeft) - parseFloat(bcs.paddingRight)) || (mobile ? 327 : 808);
    const cw = Math.max(240, boxW);
    const maxH = mobile ? c.planHeightMobile : c.planHeight;
    const padX = mobile ? 26 : 36, WALL = 3, y0 = mobile ? 24 : 32;
    const roomW = hasW ? wall : LX / 0.7, roomD = hasR ? room : DL * (IS_BED ? 1.6 : 2.4);
    const s = Math.min((cw - 2 * padX) / Math.max(roomW, fw), (maxH - y0 - 16) / Math.max(roomD, fh));
    const rw = roomW * s, rh = roomD * s, rx0 = cw / 2 - rw / 2;
    const H = Math.round(y0 + Math.max(rh + WALL, fh * s) + 16);

    // Position (cm depuis le mur gauche / le mur du haut, coin haut-gauche de l'encombrement)
    const fitX = (v, w) => { const fx = roomW - w; return fx >= 0 ? clamp(v, 0, fx) : fx / 2; };
    const fitY = (v, h) => { const fy = roomD - h; return fy >= 0 ? clamp(v, 0, fy) : 0; };
    const freeX = roomW - fw, freeY = roomD - fh;
    const posX = fitX(st.px === null ? freeX / 2 : st.px, fw);
    const posY = fitY(st.py === null ? 0 : st.py, fh);
    const sx = rx0 + posX * s, sy = y0 + posY * s;
    const cx = posX + BB.ox, cy = posY + BB.oy;

    // Distances écrites sur le plan
    const leftCm = posX, rightCm = freeX - posX, topCm = posY, bottomCm = freeY - posY;
    const midY = sy + fh * s / 2, midX = sx + fw * s / 2;
    const lab = (x, y, v, pxs, text) => ({ x: r1(x), y: r1(y), label: text || cm(v), on: v >= 1 && pxs > 44 });
    const footTxt = bench ? T.footBench : T.footBed;
    const frontTxt = ang === 0 ? (IS_BED ? footTxt : T.frontSofa) : '';
    const gL = lab(rx0 + leftCm * s / 2, midY, hasW ? leftCm : 0, leftCm * s);
    const gR = lab(sx + fw * s + rightCm * s / 2, midY, hasW ? rightCm : 0, rightCm * s);
    const gB = lab(midX, y0 + topCm * s / 2, hasR ? topCm : 0, topCm * s * 2.2);
    const gF = lab(midX, sy + fh * s + bottomCm * s / 2, hasR ? bottomCm : 0, bottomCm * s * 2.2, cm(bottomCm) + frontTxt);
    const axis = (x, y, w, h, v) => ({ x: r1(x), y: r1(y), w: r1(Math.max(0, w)), h: r1(Math.max(0, h)), on: v >= 1 });
    const lL = axis(rx0, midY, leftCm * s, 1, hasW ? leftCm : 0);
    const lR = axis(sx + fw * s, midY, rightCm * s, 1, hasW ? rightCm : 0);
    const lT = axis(midX, y0, 1, topCm * s, hasR ? topCm : 0);
    const lB = axis(midX, sy + fh * s, 1, bottomCm * s, hasR ? bottomCm : 0);

    // Verdict : encombrement actuel face aux dimensions de la pièce
    const koW = hasW && fw > wall, koD = hasR && fh > room;
    const sideFree = wall - fw, depthFree = room - fh;
    const X = cm(fw - wall), Y = cm(fh - room);
    let tone, fbTitle, fbMsg;
    if (!hasW && !hasR) { tone = 'neutral'; fbTitle = T.emptyT; fbMsg = T.emptyM; }
    else if (IS_BED) {
      if (koW && koD) { tone = 'red'; fbTitle = T.bedKoT; fbMsg = tpl(T.bedKoBoth, { x: X, y: Y }); }
      else if (koW) { tone = 'red'; fbTitle = T.bedKoT; fbMsg = tpl(T.bedKoW, { x: X }); }
      else if (koD) { tone = 'red'; fbTitle = T.bedKoT; fbMsg = tpl(T.bedKoD, { y: Y }); }
      else if (!hasW || !hasR) { tone = 'neutral'; fbTitle = tpl(T.partialT, { what: hasW ? T.partialRoomBed : T.partialWallBed }); fbMsg = T.partialM; }
      else if (sideFree < TIGHT || depthFree < PASS) { tone = 'orange'; fbTitle = T.bedTightT; fbMsg = tpl(T.bedTightM, { a: cm(sideFree / 2), b: cm(depthFree), foot: footTxt }); }
      else { tone = 'green'; fbTitle = T.bedOkT; fbMsg = tpl(T.bedOkM, { a: cm(sideFree / 2), b: cm(depthFree), foot: footTxt }); }
    } else {
      if (koW && koD) { tone = 'red'; fbTitle = bed ? T.koTBed : T.koT; fbMsg = tpl(T.koBoth, { x: X, y: Y }); }
      else if (koW) { tone = 'red'; fbTitle = bed ? T.koTBed : T.koT; fbMsg = tpl(T.koW, { x: X }) + (ang % 180 !== 0 ? T.koRotated : T.koTryRotate); }
      else if (koD) { tone = 'red'; fbTitle = bed ? T.koTBed : T.koT; fbMsg = tpl(T.koD, { y: Y }) + (bed ? T.koNeedRoom : T.koTryRotate); }
      else if (!hasW || !hasR) { tone = 'neutral'; fbTitle = tpl(T.partialT, { what: hasW ? T.partialRoomSofa : T.partialWallSofa }); fbMsg = T.partialM; }
      else if (sideFree < TIGHT || depthFree < PASS) { tone = 'orange'; fbTitle = bed ? T.tightTBed : T.tightT; fbMsg = tpl(T.tightM, { a: cm(sideFree), b: cm(depthFree), p: PASS }); }
      else { tone = 'green'; fbTitle = bed ? T.okTBed : T.okT; fbMsg = tpl(T.okM, { a: cm(sideFree), b: cm(depthFree) }); }
    }

    // Porte : ouverture dans un mur + zone de débattement que le produit ne doit pas toucher
    const hinge = st.doorHinge === 'end' ? 'end' : 'start';
    const dwRaw = parseInt(st.doorW, 10), dw = (isNaN(dwRaw) || dwRaw < 40) ? 0 : Math.min(dwRaw, 300);
    const dWall = st.doorWall || 'bottom';
    const wallLen = (w) => ((w === 'top' || w === 'bottom') ? roomW : roomD);
    let dPos = st.doorPos === null ? wallLen(dWall) - dw - 20 : st.doorPos;
    dPos = clamp(dPos, 0, Math.max(0, wallLen(dWall) - dw));
    const sq = dWall === 'bottom' ? [dPos, roomD - dw, dw, dw] : dWall === 'top' ? [dPos, 0, dw, dw] : dWall === 'left' ? [0, dPos, dw, dw] : [roomW - dw, dPos, dw, dw];
    function hitRect(r) {
      const axes = [[1, 0], [0, 1], [COS, SIN], [-SIN, COS]];
      const rc = [[r[0], r[1]], [r[0] + r[2], r[1]], [r[0], r[1] + r[3]], [r[0] + r[2], r[1] + r[3]]];
      return local.some((qq) => {
        const pc = [[qq[0], qq[1]], [qq[0] + qq[2], qq[1]], [qq[0], qq[1] + qq[3]], [qq[0] + qq[2], qq[1] + qq[3]]].map((k) => {
          const x = k[0] - LX / 2, y = k[1] - DL / 2; return [cx + x * COS - y * SIN, cy + x * SIN + y * COS];
        });
        return axes.every((a) => {
          const pr = (pts) => { let mn = Infinity, mx = -Infinity; pts.forEach((k) => { const v = k[0] * a[0] + k[1] * a[1]; mn = Math.min(mn, v); mx = Math.max(mx, v); }); return [mn, mx]; };
          const A = pr(pc), Bq = pr(rc); return A[1] > Bq[0] + 1 && Bq[1] > A[0] + 1;
        });
      });
    }
    const sizeTone = tone;
    const doorBlocked = dw > 0 && hasW && hasR && hitRect(sq);
    if (doorBlocked && tone !== 'red') {
      tone = 'orange'; fbTitle = T.doorT;
      fbMsg = tpl(T.doorM, { v: cm(dw), kind: IS_BED ? T.kindBedLow : T.kindSofaLow });
    }

    // Mémorise la géométrie pour les gestes et les boutons
    this.g = {
      cw, s, rx0, y0, roomW, roomD, dw, dWall, dPos, angRaw, ang, cx, cy, posX, posY, freeX, freeY, fw, fh,
      fitX, fitY, bbox, local, LX, DL, SIN, COS, wall, room, DEF_WALL, DEF_ROOM,
      bed, pouf, bench, sofaDepth, bedDepth, SZ, EXW
    };

    /* ── Application au DOM ── */
    const set = (el, o) => { if (!el) return; for (const k in o) el.style[k] = o[k]; };
    const pressed = (el, on) => {
      if (!el) return;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    const text = (name, v) => { const el = this.q(name); if (el && el.textContent !== v) el.textContent = v; };
    const show = (name, on) => { const el = this.q(name); if (el) el.hidden = !on; };

    this.stage.classList.toggle('is-anim', !noAnim && !st.dragging && !st.doorDragging);

    // En-tête produit
    const titleHasPouf = /pouf|poef/i.test(c.title);
    text('productTitle', c.title + (pouf && !titleHasPouf ? T.plusPouf : '') + (bench ? T.plusBench : ''));
    text('spec1Value', cm(LX));
    text('spec2Value', IS_BED ? cm(SZ.l + EXL) : cm(D));
    show('spec3Wrap', IS_BED || CONV);
    text('spec3', T.specSleep);
    text('spec3Value', IS_BED ? sizeLabel : Math.round(P.bedLength) + ' × ' + Math.round(BEDW) + ' cm');

    // Champs
    const syncInput = (el, v) => { if (el && document.activeElement !== el && el.value !== v) el.value = v; };
    syncInput(this.wallInput, wallStr);
    syncInput(this.roomInput, roomStr);
    syncInput(this.doorInput, st.doorW);
    this.wallInput.placeholder = tpl(T.example, { v: DEF_WALL });
    this.roomInput.placeholder = tpl(T.example, { v: DEF_ROOM });
    const shell = (ko) => (ko ? 'mc-shell is-error' : (tone === 'green' ? 'mc-shell is-success' : 'mc-shell'));
    this.q('wallShell').className = shell(koW);
    this.q('roomShell').className = shell(koD);

    // Options
    show('dirGroup', !IS_BED && SHAPE === 'corner');
    show('convGroup', CONV);
    show('sofaOpts', !IS_BED && (SHAPE === 'corner' || CONV));
    pressed(this.q('dirLeft'), dir === 'left');
    pressed(this.q('dirRight'), dir === 'right');
    pressed(this.q('cfgSeat'), !bed);
    pressed(this.q('cfgBed'), bed);
    show('poufGroup', POUF_OK);
    pressed(this.q('poufOff'), !pouf);
    pressed(this.q('poufOn'), pouf);
    show('benchGroup', IS_BED && !!c.bench);
    pressed(this.q('benchOff'), !bench);
    pressed(this.q('benchOn'), bench);
    if (this.sizeBtns) this.sizeBtns.forEach((b, i) => pressed(b, this.sizes[i].key === SZ.key));
    const vert = dWall === 'left' || dWall === 'right';
    text('hingeA', vert ? T.hingeTop : T.left);
    text('hingeB', vert ? T.hingeBottom : T.right);
    pressed(this.q('hingeA'), hinge === 'start');
    pressed(this.q('hingeB'), hinge === 'end');
    text('doorHelp', mobile ? T.doorHelpMobile : T.doorHelp);

    // Plan
    text('dragHint', IS_BED ? (mobile ? T.dragBedMobile : T.dragBed) : (mobile ? T.dragSofaMobile : T.dragSofa));
    text('angLabel', ang + '°');
    set(this.stage, { width: px(cw), height: px(H) });
    set(this.q('room'), { left: px(r1(rx0 - WALL)), top: px(r1(y0 - WALL)), width: px(r1(rw)), height: px(r1(rh)), borderStyle: (hasW && hasR) ? 'solid' : 'dashed' });
    const lw = this.q('lblW'); text('lblW', hasW ? cm(wall) : (IS_BED ? T.wallBed : T.wallSofa)); set(lw, { left: px(r1(cw / 2)), top: px(r1(y0 - WALL - 12)) });
    const lr = this.q('lblR'); text('lblR', hasR ? cm(room) : (IS_BED ? T.roomShortBed : T.roomShortSofa)); set(lr, { left: px(r1(rx0 + rw + WALL + 14)), top: px(r1(y0 + rh / 2)) });
    [['lL', lL], ['lR', lR], ['lT', lT], ['lB', lB]].forEach(([n, a]) => set(this.q(n), { left: px(a.x), top: px(a.y), width: px(a.w), height: px(a.h), display: a.on ? 'block' : 'none' }));
    [['gL', gL], ['gR', gR], ['gB', gB], ['gF', gF]].forEach(([n, a]) => { text(n, a.label); set(this.q(n), { left: px(a.x), top: px(a.y), display: a.on ? 'block' : 'none' }); });

    // Porte : battant en trait plein, débattement en arc pointillé (orange si le produit le gêne)
    const door = this.q('door'), X0 = rx0 + sq[0] * s, Y0 = y0 + sq[1] * s, DWs = dw * s, stt = hinge === 'start';
    const arc = '1px dashed ' + (doorBlocked ? 'var(--color-border-orange-dark)' : 'var(--color-border-neutral-5)');
    const TD = {
      bottom: { start: ['0 100% 0 0', ['borderTop', 'borderRight']], end: ['100% 0 0 0', ['borderTop', 'borderLeft']] },
      top: { start: ['0 0 100% 0', ['borderBottom', 'borderRight']], end: ['0 0 0 100%', ['borderBottom', 'borderLeft']] },
      left: { start: ['0 0 100% 0', ['borderBottom', 'borderRight']], end: ['0 100% 0 0', ['borderTop', 'borderRight']] },
      right: { start: ['0 0 0 100%', ['borderBottom', 'borderLeft']], end: ['100% 0 0 0', ['borderTop', 'borderLeft']] }
    }[dWall][hinge];
    const doorKey = dWall + hinge;
    if (this._doorKey !== doorKey) {
      // Changement de mur ou de sens : la porte est redessinée en fondu, sans glisser
      this._doorKey = doorKey;
      [door, this.q('leaf')].forEach((el) => { el.classList.remove('swf-door-in'); void el.offsetWidth; el.classList.add('swf-door-in'); });
    }
    set(door, { display: dw > 0 ? 'flex' : 'none', left: px(r1(X0)), top: px(r1(Y0)), width: px(r1(DWs)), height: px(r1(DWs)),
      borderTop: '0', borderRight: '0', borderBottom: '0', borderLeft: '0', borderRadius: TD[0], cursor: st.doorDragging ? 'grabbing' : 'grab' });
    door.style[TD[1][0]] = arc; door.style[TD[1][1]] = arc;
    door.setAttribute('aria-label', tpl(T.doorAria, { v: cm(dw), w: T.walls[dWall] }));
    text('doorLabel', tpl(T.door, { v: cm(dw) }));
    set(this.q('doorLabel'), { display: DWs > 64 ? 'block' : 'none' });
    let leaf, op;
    if (dWall === 'bottom') { leaf = { x: stt ? X0 - WALL : X0 + DWs, y: y0 + rh - DWs, w: WALL, h: DWs + WALL }; op = { x: X0 - 1, y: y0 + rh - 1, w: DWs + 2, h: WALL + 2 }; }
    if (dWall === 'top') { leaf = { x: stt ? X0 - WALL : X0 + DWs, y: y0 - WALL, w: WALL, h: DWs + WALL }; op = { x: X0 - 1, y: y0 - WALL - 1, w: DWs + 2, h: WALL + 2 }; }
    if (dWall === 'left') { leaf = { x: rx0 - WALL, y: stt ? Y0 - WALL : Y0 + DWs, w: DWs + WALL, h: WALL }; op = { x: rx0 - WALL - 1, y: Y0 - 1, w: WALL + 2, h: DWs + 2 }; }
    if (dWall === 'right') { leaf = { x: rx0 + rw - DWs, y: stt ? Y0 - WALL : Y0 + DWs, w: DWs + WALL, h: WALL }; op = { x: rx0 + rw - 1, y: Y0 - 1, w: WALL + 2, h: DWs + 2 }; }
    const showDoor = dw > 0 ? 'block' : 'none';
    set(this.q('leaf'), { display: showDoor, left: px(r1(leaf.x)), top: px(r1(leaf.y)), width: px(r1(leaf.w)), height: px(r1(leaf.h)) });
    set(this.q('opening'), { display: showDoor, left: px(r1(op.x)), top: px(r1(op.y)), width: px(r1(op.w)), height: px(r1(op.h)) });

    // Produit : prend la couleur du verdict (teintes foncées Meridian)
    const TONES = {
      neutral: { fill: 'var(--color-background-neutral-6)', ink: 'var(--color-foreground-neutral-0)' },
      green: { fill: 'var(--color-background-green-dark)', ink: 'var(--color-foreground-neutral-0)' },
      orange: { fill: 'var(--color-background-orange-dark)', ink: 'var(--color-foreground-neutral-0)' },
      red: { fill: 'var(--color-background-red-dark)', ink: 'var(--color-foreground-neutral-0)' }
    };
    const tc = TONES[tone];
    set(this.sofaEl, { left: px(r1(rx0 + (cx - LX / 2) * s)), top: px(r1(y0 + (cy - DL / 2) * s)), width: px(r1(LX * s)), height: px(r1(DL * s)),
      transform: 'rotate(' + angRaw + 'deg)', cursor: st.dragging ? 'grabbing' : 'grab' });
    this.sofaEl.setAttribute('aria-label', tpl(T.sofaAria, { kind: IS_BED ? T.kindBed : T.kindSofa, name: NAME, unf: bed ? T.unfolded : '', l: cm(leftCm), r: cm(rightCm), a: ang }));
    while (this.piecesEl.children.length < local.length) this.piecesEl.appendChild(document.createElement('div'));
    while (this.piecesEl.children.length > local.length) this.piecesEl.lastChild.remove();
    local.forEach((qq, i) => {
      const el = this.piecesEl.children[i];
      const isPouf = i === poufIdx;
      set(el, { left: px(r1(qq[0] * s + 1)), top: px(r1(qq[1] * s + 1)), width: px(r1(Math.max(0, qq[2] * s - 2))), height: px(r1(Math.max(0, qq[3] * s - 2))),
        background: isPouf ? 'transparent' : tc.fill, border: isPouf ? '2px solid ' + tc.fill : '0' });
    });
    const lq = mir(label);
    const seat = this.q('seat');
    set(seat, { left: px(r1((lq[0] + lq[2] / 2) * s)), top: px(r1((lq[1] + lq[3] / 2) * s)), transform: 'translate(-50%, -50%) rotate(' + (-angRaw) + 'deg)',
      display: (lq[2] * s > 120 && lq[3] * s > 40) ? 'flex' : 'none', color: tc.ink });
    text('seatName', NAME);
    text('seatSize', sizeLabel);

    // Récapitulatif sous le plan (mobile)
    const sum = this.q('summary');
    if (sum) {
      const items = [[T.sumWall, hasW ? cm(wall) : '–'], [T.sumRoom, hasR ? cm(room) : '–']]
        .concat(IS_BED ? [[T.sumBed, sizeLabel]].concat(c.bench ? [[T.sumBench, bench ? T.yes : T.no]] : [])
          : (SHAPE === 'corner' ? [[T.sumMeri, dir === 'right' ? T.sumRight : T.sumLeft]] : []).concat(bed ? [[T.config, T.sumUnfolded]] : []).concat(pouf ? [[T.sumPouf, T.yes]] : []))
        .concat([[T.sumDoor, cm(dw)]]);
      const html = items.map((it) => '<span>' + it[0] + ' <strong>' + it[1] + '</strong></span>').join('');
      if (sum.innerHTML !== html) sum.innerHTML = html;
    }

    // Verdict
    const fb = this.q('fb');
    fb.className = 'mc-fb ' + tone;
    ['neutral', 'green', 'orange', 'red'].forEach((t) => show('ico-' + t, t === tone));
    text('fbTitle', fbTitle);
    text('fbMsg', fbMsg);
    text('footTitle', fbTitle);
    const ft = this.q('footTitle');
    if (ft) ft.style.color = ({ neutral: 'var(--color-foreground-neutral-10)', green: 'var(--color-foreground-green-dark)', orange: 'var(--color-foreground-orange-dark)', red: 'var(--color-foreground-red-dark)' })[tone];
    const altUrl = IS_BED ? c.altBedUrl : c.altSofaUrl;
    show('altWrap', !!altUrl && (sizeTone === 'red' || sizeTone === 'orange'));
    const alt = this.q('alt');
    if (alt && altUrl) { alt.href = altUrl; text('altLabel', IS_BED ? T.altBed : T.altSofa); }

    // Calques
    const sch = this.q('schemaPanel');
    if (sch) {
      sch.classList.toggle('is-open', st.schema); sch.setAttribute('aria-hidden', st.schema ? 'false' : 'true');
      this.q('schemaScrim').classList.toggle('is-open', st.schema);
    }
    const form = this.q('form');
    form.classList.toggle('is-open', st.opts);
    form.setAttribute('aria-hidden', (mobile && !st.opts) ? 'true' : 'false');
    if (mobile) { form.setAttribute('role', 'dialog'); form.setAttribute('aria-modal', 'true'); }
    else { form.removeAttribute('role'); form.removeAttribute('aria-modal'); }
    this.q('optsScrim').classList.toggle('is-open', st.opts && mobile);
  }
}

if (!customElements.get('pk-room-fit')) customElements.define('pk-room-fit', PkRoomFit);
